import type { RefMeta } from '@/context/references/references-context';
import { resolveRefMeta } from '@/lib/edit-text-serialize';

const REF_TOKEN_RE = /\{\{ref:([^}]+)\}\}/g;

/**
 * In-place DOM patch: pair each list item's `.ref-chip` elements (DOM order)
 * with the `{{ref:UUID}}` tokens in that item's stored text (same order),
 * and repaint `data-ref-id` / label / `data-broken` / `data-lost` from
 * `refMap`. Mirrors the paragraph cluster's chip-reconciliation effect —
 * needed because the list editor renders chips once at EditorJS init with
 * whatever `refMap` exists then, and never repaints when the references
 * query resolves afterward (e.g. on a cold page load).
 */
export function reconcileListChips(
  itemEls: readonly HTMLElement[],
  items: readonly string[],
  refMap: Map<string, RefMeta>,
  sourceBlockId?: string,
): void {
  itemEls.forEach((itemEl, i) => {
    const text = items[i] ?? '';
    const tokenIds: string[] = [];
    for (const match of text.matchAll(REF_TOKEN_RE)) {
      tokenIds.push(match[1]);
    }
    const chips = itemEl.querySelectorAll<HTMLElement>(
      '.ref-chip[data-ref-id]',
    );
    chips.forEach((chip, j) => {
      const targetId = tokenIds[j] ?? chip.getAttribute('data-ref-id') ?? '';
      if (chip.getAttribute('data-ref-id') !== targetId) {
        chip.setAttribute('data-ref-id', targetId);
      }
      const meta = resolveRefMeta(refMap, targetId, sourceBlockId);
      if (meta) {
        chip.textContent = meta.name;
        chip.removeAttribute('data-broken');
        if (meta.lost) {
          chip.setAttribute('data-lost', 'true');
        } else {
          chip.removeAttribute('data-lost');
        }
      } else {
        chip.setAttribute('data-broken', 'true');
        chip.removeAttribute('data-lost');
        chip.textContent = targetId;
      }
    });
  });
}
